import type { PlatformRole } from '@vlm/api';
import {
  AppShell,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  Logo,
  PageLoader,
  Rail,
  RailItem,
  RailLogo,
  ScreenLoader,
  Sheet,
  TopBar,
  cn,
  prefs,
  usePrefs,
  useStoredFlag,
  type Theme,
} from '@vlm/ui';
import { ChevronDown, ExternalLink, Globe, LogOut, Menu, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { Suspense, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { env } from '../env';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { restoreSession, signOut, useAuth, useUser } from '../lib/auth';
import { visibleNav } from './nav';

/** Signed in, or on the way to the sign-in page. */
export function Layout() {
  const { t } = useT();
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === 'checking') return <ScreenLoader label={t('auth.checking')} />;
  if (status === 'unreachable')
    return (
      <div className="grid h-dvh place-items-center bg-canvas p-4">
        <EmptyState title={t('auth.unreachableTitle')} body={t('auth.unreachableBody')} action={<Button onClick={() => void restoreSession()}>{t('common.retry')}</Button>} />
      </div>
    );
  if (status === 'signedOut') return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  return <Shell />;
}

/** A page only some roles may open; the others see why, not a silent redirect. */
export function RequireRole({ roles, children }: { roles: readonly PlatformRole[]; children: ReactNode }) {
  const { t } = useT();
  const role = useAuth((s) => s.user?.role);
  if (role && roles.includes(role)) return <>{children}</>;
  return (
    <div className="grid place-items-center px-4 py-16">
      <EmptyState
        title={t('pages.forbiddenTitle')}
        body={t('pages.forbiddenBody')}
        action={
          <Button asChild variant="secondary">
            <Link to="/overview">{t('pages.goHome')}</Link>
          </Button>
        }
      />
    </div>
  );
}

function LanguageToggle() {
  const { t, lang } = useT();
  const user = useUser();
  const flip = () => {
    const next = lang === 'ar' ? 'en' : 'ar';
    prefs.setLang(next);
    // the user's language follows them to any device; a failure only costs that
    if (user) void api.me.update({ fullName: user.fullName, email: user.email, language: next }).catch(() => {});
  };
  return (
    <Button variant="quiet" size="sm" onClick={flip} aria-label={t('shell.switchLanguageLabel')} lang={lang === 'ar' ? 'en' : 'ar'} className="px-2.5">
      {t('shell.switchLanguage')}
    </Button>
  );
}

const THEMES: Array<{ value: Theme; icon: ReactNode; label: 'shell.themeDay' | 'shell.themeNight' | 'shell.themeAuto' }> = [
  { value: 'day', icon: <Sun size={16} />, label: 'shell.themeDay' },
  { value: 'night', icon: <Moon size={16} />, label: 'shell.themeNight' },
  { value: 'auto', icon: <Monitor size={16} />, label: 'shell.themeAuto' },
];

function UserMenu() {
  const { t } = useT();
  const user = useUser();
  const navigate = useNavigate();
  const theme = usePrefs((p) => p.theme);
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex h-control-md items-center gap-2.5 rounded-md pe-1.5 ps-1 text-start transition-colors duration-quick hover:bg-hover" aria-label={t('shell.account')}>
          <Avatar name={user.fullName || user.username} size={30} />
          <span className="hidden min-w-0 leading-tight lg:block">
            <span className="block max-w-[180px] truncate text-body-sm font-bold text-ink">{user.fullName || user.username}</span>
            <span className="block text-[12px] text-ink-subtle">{t(`roles.${user.role}`)}</span>
          </span>
          <ChevronDown size={16} className="hidden text-ink-subtle lg:block" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <div className="flex items-center gap-3 px-2.5 pb-2 pt-1.5">
          <Avatar name={user.fullName || user.username} size={36} />
          <div className="min-w-0">
            <p className="truncate text-body-sm font-bold text-ink">{user.fullName || user.username}</p>
            <p className="truncate font-mono text-[12px] text-ink-subtle" dir="ltr">
              {user.username}
            </p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<UserRound size={16} />} onSelect={() => navigate('/profile')}>
          {t('nav.profile')}
        </DropdownMenuItem>
        <DropdownMenuItem icon={<ExternalLink size={16} />} onSelect={() => window.open(env.adminUrl, '_blank', 'noopener')}>
          {t('shell.openAdmin')}
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Globe size={16} />} onSelect={() => window.open(env.websiteUrl, '_blank', 'noopener')}>
          {t('shell.openWebsite')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('shell.theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => prefs.setTheme(v as Theme)}>
          {THEMES.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value} icon={o.icon}>
              {t(o.label)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut size={16} />}
          onSelect={() => {
            signOut();
            navigate('/login', { replace: true });
          }}
        >
          {t('shell.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNav({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useT();
  const role = useAuth((s) => s.user?.role);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={<Logo height={30} />} closeLabel={t('common.close')} width={300}>
      <nav aria-label={t('app.navigation')} className="grid gap-1 p-3">
        {visibleNav(role).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => onOpenChange(false)}
            className={({ isActive }) =>
              cn(
                'relative flex h-11 items-center gap-3 rounded-md px-3 text-body font-semibold no-underline',
                isActive ? 'bg-selected text-selected-ink before:absolute before:inset-y-1.5 before:start-0 before:w-[3px] before:rounded-full before:bg-selected-line' : 'text-ink hover:bg-hover',
              )
            }
          >
            <item.icon size={20} aria-hidden="true" />
            {t(item.label)}
          </NavLink>
        ))}
      </nav>
    </Sheet>
  );
}

function Shell() {
  const { t } = useT();
  const role = useAuth((s) => s.user?.role);
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  // icons with their names, or icons alone: the reader's choice, kept on this device
  const [railOpen, setRailOpen] = useStoredFlag('vlm.rail.expanded', true);

  const rail = (
    <Rail
      label={t('app.navigation')}
      expanded={railOpen}
      onExpandedChange={setRailOpen}
      expandLabel={t('shell.expandMenu')}
      collapseLabel={t('shell.collapseMenu')}
      logo={
        <Link to="/overview" aria-label={t('nav.dashboard')}>
          <RailLogo />
        </Link>
      }
    >
      {visibleNav(role).map((item) => (
        <RailItem key={item.to} asChild label={t(item.label)} icon={<item.icon size={20} aria-hidden="true" />} active={location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)}>
          <NavLink to={item.to} />
        </RailItem>
      ))}
    </Rail>
  );

  const topBar = (
    <TopBar>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-tooltip focus:rounded-md focus:bg-surface focus:px-3 focus:py-2">
        {t('app.skip')}
      </a>
      <IconButton className="md:hidden" label={t('app.menu')} icon={<Menu size={20} aria-hidden="true" />} onClick={() => setMenu(true)} />
      <p className="hidden font-display text-title text-ink sm:block">{t('app.name')}</p>
      <div className="ms-auto flex items-center gap-1 sm:gap-2">
        <LanguageToggle />
        <UserMenu />
      </div>
    </TopBar>
  );

  return (
    <AppShell rail={rail} topBar={topBar}>
      <MobileNav open={menu} onOpenChange={setMenu} />
      <Suspense fallback={<PageLoader label={t('common.loading')} />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}
